import React, { useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Switch,
  FormControlLabel
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import BlockIcon from '@mui/icons-material/Block';
import { Customer } from '../../firebase/services';

interface User {
  id: string;
  email: string;
  displayName: string;
  jobTitle?: string;
  avatarUrl?: string;
  roleId: number;
  customerId?: string;
  isDisabled?: boolean;
  createdAt: Date;
}

interface UsersListProps {
  users: User[];
  customers: Customer[];
  loading: boolean;
  onLinkUser?: (userId: string, customerId: string) => Promise<boolean>;
  onUnlinkUser?: (userId: string) => Promise<boolean>;
  onUpdateUser?: (userId: string, userData: Partial<User>) => Promise<boolean>;
  onResetPassword?: (email: string) => Promise<boolean>;
  onDisableUser?: (userId: string) => Promise<boolean>;
  onEnableUser?: (userId: string) => Promise<boolean>;
}

const UsersList: React.FC<UsersListProps> = ({ 
  users, 
  customers, 
  loading, 
  onLinkUser, 
  onUnlinkUser,
  onUpdateUser,
  onResetPassword,
  onDisableUser,
  onEnableUser
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [processingLink, setProcessingLink] = useState(false);
  const [processingEdit, setProcessingEdit] = useState(false);
  const [processingReset, setProcessingReset] = useState(false);
  const [processingDisable, setProcessingDisable] = useState(false);
  
  // État pour l'édition d'un utilisateur
  const [editUserData, setEditUserData] = useState({
    displayName: '',
    email: '',
    roleId: 0,
    jobTitle: '',
    avatarUrl: ''
  });
  
  // Filtrer les utilisateurs en fonction du terme de recherche
  const filteredUsers = users.filter(user => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      user.displayName.toLowerCase().includes(searchTermLower) ||
      user.email.toLowerCase().includes(searchTermLower) ||
      (user.customerId && 
        customers.find(c => c.id === user.customerId)?.companyName.toLowerCase().includes(searchTermLower))
    );
  });

  // Obtenir le libellé du rôle
  const getRoleLabel = (roleId: number) => {
    switch (roleId) {
      case 1:
        return 'Administrateur';
      case 2:
        return 'Client';
      default:
        return 'Inconnu';
    }
  };

  // Formater la date
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('fr-FR', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  };

  // Handle opening the link dialog
  const handleOpenLinkDialog = (user: User) => {
    setSelectedUser(user);
    setSelectedCustomerId(user.customerId || '');
    setShowLinkDialog(true);
  };

  // Handle opening the edit dialog
  const handleOpenEditDialog = (user: User) => {
    setSelectedUser(user);
    setEditUserData({
      displayName: user.displayName,
      email: user.email,
      roleId: user.roleId,
      jobTitle: user.jobTitle || '',
      avatarUrl: user.avatarUrl || ''
    });
    setShowEditDialog(true);
  };

  // Handle opening the reset password dialog
  const handleOpenResetDialog = (user: User) => {
    setSelectedUser(user);
    setShowResetDialog(true);
  };

  // Handle customer selection change
  const handleCustomerChange = (event: SelectChangeEvent) => {
    setSelectedCustomerId(event.target.value);
  };

  // Handle role selection change
  const handleRoleChange = (event: SelectChangeEvent) => {
    setEditUserData({
      ...editUserData,
      roleId: Number(event.target.value)
    });
  };

  // Handle edit form field change
  const handleEditChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditUserData({
      ...editUserData,
      [field]: event.target.value
    });
  };

  // Handle linking a user to a customer
  const handleLinkUser = async () => {
    if (!selectedUser || !onLinkUser || !selectedCustomerId) return;
    
    setProcessingLink(true);
    try {
      const success = await onLinkUser(selectedUser.id, selectedCustomerId);
      if (success) {
        // Mettre à jour l'utilisateur dans la liste locale
        const updatedUsers = users.map(user => {
          if (user.id === selectedUser.id) {
            return {
              ...user,
              customerId: selectedCustomerId
            };
          }
          return user;
        });
        
        // Trouver le nom du client pour l'affichage
        const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
        
        // Afficher un message de succès
        console.log(`User ${selectedUser.displayName} linked to customer ${selectedCustomer?.companyName || selectedCustomerId}`);
      } else {
        console.error("Failed to link user to customer");
      }
    } catch (error) {
      console.error("Error linking user to customer:", error);
    } finally {
      setProcessingLink(false);
      setShowLinkDialog(false);
    }
  };

  // Handle unlinking a user from a customer
  const handleUnlinkUser = async (user: User) => {
    if (!onUnlinkUser || !user.customerId) return;
    
    try {
      await onUnlinkUser(user.id);
    } catch (error) {
      console.error("Error unlinking user from customer:", error);
    }
  };

  // Handle updating a user
  const handleUpdateUser = async () => {
    if (!selectedUser || !onUpdateUser) return;
    
    setProcessingEdit(true);
    try {
      const success = await onUpdateUser(selectedUser.id, editUserData);
      if (success) {
        console.log(`User ${selectedUser.displayName} updated successfully`);
      } else {
        console.error("Failed to update user");
      }
    } catch (error) {
      console.error("Error updating user:", error);
    } finally {
      setProcessingEdit(false);
      setShowEditDialog(false);
    }
  };

  // Handle resetting a user's password
  const handleResetPassword = async () => {
    if (!selectedUser || !onResetPassword) return;
    
    setProcessingReset(true);
    try {
      const success = await onResetPassword(selectedUser.email);
      if (success) {
        console.log(`Password reset email sent to ${selectedUser.email}`);
      } else {
        console.error("Failed to send password reset email");
      }
    } catch (error) {
      console.error("Error resetting password:", error);
    } finally {
      setProcessingReset(false);
      setShowResetDialog(false);
    }
  };

  // Handle toggling user's disabled status
  const handleToggleUserStatus = async (user: User) => {
    if ((!onDisableUser && !user.isDisabled) || (!onEnableUser && user.isDisabled)) return;
    
    setProcessingDisable(true);
    try {
      let success;
      if (user.isDisabled) {
        // Activer l'utilisateur
        if (onEnableUser) {
          success = await onEnableUser(user.id);
        }
      } else {
        // Désactiver l'utilisateur
        if (onDisableUser) {
          success = await onDisableUser(user.id);
        }
      }
      
      if (success) {
        console.log(`User ${user.displayName} ${user.isDisabled ? 'enabled' : 'disabled'} successfully`);
      } else {
        console.error(`Failed to ${user.isDisabled ? 'enable' : 'disable'} user`);
      }
    } catch (error) {
      console.error(`Error ${user.isDisabled ? 'enabling' : 'disabling'} user:`, error);
    } finally {
      setProcessingDisable(false);
    }
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 3,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <GroupIcon />
        Liste des utilisateurs
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Rechercher un utilisateur..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          variant="outlined"
          size="small"
        />
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Avatar</TableCell>
                <TableCell>Nom</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Titre</TableCell>
                <TableCell>Rôle</TableCell>
                <TableCell>Client associé</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Créé le</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <TableRow key={user.id} sx={{ bgcolor: user.isDisabled ? 'action.disabledBackground' : 'inherit' }}>
                    <TableCell>
                      <Avatar src={user.avatarUrl} alt={user.displayName}>
                        {user.displayName.charAt(0).toUpperCase()}
                      </Avatar>
                    </TableCell>
                    <TableCell>{user.displayName}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.jobTitle || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getRoleLabel(user.roleId)} 
                        color={user.roleId === 1 ? "primary" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user.customerId ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon fontSize="small" color="primary" />
                          <Typography variant="body2">
                            {customers.find(c => c.id === user.customerId)?.companyName || 'Client inconnu'}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Non associé
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={user.isDisabled ? "Bloqué" : "Actif"} 
                        color={user.isDisabled ? "error" : "success"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Modifier l'utilisateur">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleOpenEditDialog(user)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Réinitialiser le mot de passe">
                          <IconButton 
                            size="small" 
                            color="warning"
                            onClick={() => handleOpenResetDialog(user)}
                          >
                            <LockResetIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title={user.isDisabled ? "Débloquer l'utilisateur" : "Bloquer l'utilisateur"}>
                          <IconButton 
                            size="small" 
                            color={user.isDisabled ? "success" : "error"}
                            onClick={() => handleToggleUserStatus(user)}
                            disabled={processingDisable}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {user.customerId ? (
                          <Tooltip title="Dissocier du client">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleUnlinkUser(user)}
                            >
                              <LinkOffIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Associer à un client">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenLinkDialog(user)}
                            >
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {searchTerm ? 'Aucun utilisateur ne correspond à votre recherche' : 'Aucun utilisateur trouvé'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog to link user to a customer */}
      <Dialog 
        open={showLinkDialog} 
        onClose={() => setShowLinkDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Associer {selectedUser?.displayName} à un client
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="customer-select-label">Client</InputLabel>
              <Select
                labelId="customer-select-label"
                value={selectedCustomerId}
                label="Client"
                onChange={handleCustomerChange}
                disabled={processingLink}
              >
                <MenuItem value="">
                  <em>Aucun</em>
                </MenuItem>
                {customers
                  .filter(c => c.status === 'active')
                  .map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.companyName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowLinkDialog(false)}
            disabled={processingLink}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleLinkUser}
            variant="contained" 
            disabled={!selectedCustomerId || processingLink}
          >
            Associer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog to edit user */}
      <Dialog 
        open={showEditDialog} 
        onClose={() => setShowEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Modifier {selectedUser?.displayName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {editUserData.avatarUrl && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Avatar 
                  src={editUserData.avatarUrl} 
                  alt={editUserData.displayName}
                  sx={{ width: 80, height: 80 }}
                />
              </Box>
            )}
            <TextField
              fullWidth
              label="Nom d'utilisateur"
              value={editUserData.displayName}
              onChange={handleEditChange('displayName')}
              disabled={processingEdit}
            />
            <TextField
              fullWidth
              label="Titre / Fonction"
              value={editUserData.jobTitle}
              onChange={handleEditChange('jobTitle')}
              disabled={processingEdit}
              placeholder="CEO, CTO, Director, Team Leader..."
            />
            <TextField
              fullWidth
              label="URL de l'avatar"
              value={editUserData.avatarUrl}
              onChange={handleEditChange('avatarUrl')}
              disabled={processingEdit}
              placeholder="https://example.com/avatar.jpg"
            />
            <TextField
              fullWidth
              label="Email"
              value={editUserData.email}
              onChange={handleEditChange('email')}
              disabled={processingEdit}
            />
            <FormControl fullWidth>
              <InputLabel id="role-select-label">Rôle</InputLabel>
              <Select
                labelId="role-select-label"
                value={editUserData.roleId.toString()}
                label="Rôle"
                onChange={handleRoleChange}
                disabled={processingEdit}
              >
                <MenuItem value="1">Administrateur</MenuItem>
                <MenuItem value="2">Client</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowEditDialog(false)}
            disabled={processingEdit}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleUpdateUser}
            variant="contained" 
            disabled={processingEdit}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog to reset password */}
      <Dialog 
        open={showResetDialog} 
        onClose={() => setShowResetDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Réinitialiser le mot de passe
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography>
              Voulez-vous envoyer un email de réinitialisation de mot de passe à {selectedUser?.email} ?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowResetDialog(false)}
            disabled={processingReset}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleResetPassword}
            variant="contained" 
            color="warning"
            disabled={processingReset}
          >
            Envoyer l'email
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UsersList; 